import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthLayout } from './AuthLayout';
import styles from './AuthLayout.module.css';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { ApiError } from '@/lib/apiClient';
import { authApi } from '@/lib/authApi';

/**
 * Troca a senha com o token do e-mail (RF-01.05).
 *
 * O subtítulo avisa que todas as sessões caem. Quem redefine senha costuma
 * estar reagindo a um acesso indevido e precisa saber que o outro dispositivo
 * foi desconectado — é a informação mais útil da tela.
 */
export function ResetPasswordPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [fieldErrors, setFieldErrors] = useState<readonly string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setFieldErrors([]);

    // Comparação entre dois campos do próprio formulário, no navegador do
    // usuário: não há segredo do servidor envolvido nem canal para medir tempo.
    // eslint-disable-next-line security/detect-possible-timing-attacks
    if (password !== confirmation) {
      setFieldErrors(['As duas senhas precisam ser iguais.']);

      return;
    }

    setIsSubmitting(true);

    try {
      await authApi.resetPassword(token, password);
      navigate('/entrar', { replace: true, state: { passwordReset: true } });
    } catch (caught) {
      if (caught instanceof ApiError && caught.isValidation) {
        setFieldErrors(caught.fieldErrors('newPassword'));
      } else {
        setError('Este link é inválido ou já expirou. Peça um novo.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  // Verifica presença do token na URL, não igualdade com segredo.
  // eslint-disable-next-line security/detect-possible-timing-attacks
  if (token === '') {
    return (
      <AuthLayout footer={<Link to="/esqueci-senha">Pedir novo link</Link>} title="Link inválido">
        <Alert tone="error">Este endereço não traz um token de redefinição.</Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      footer={<Link to="/entrar">Voltar para o acesso</Link>}
      subtitle="Escolher uma senha nova encerra todas as sessões abertas."
      title="Criar senha nova"
    >
      <form className={styles.form} noValidate onSubmit={(event) => void handleSubmit(event)}>
        {error !== null && <Alert tone="error">{error}</Alert>}

        <Field
          autoComplete="new-password"
          errors={fieldErrors}
          hint="Mínimo de 10 caracteres."
          label="Nova senha"
          name="newPassword"
          onChange={(event) => setPassword(event.target.value)}
          required
          type="password"
          value={password}
        />

        <Field
          autoComplete="new-password"
          label="Repita a nova senha"
          name="confirmation"
          onChange={(event) => setConfirmation(event.target.value)}
          required
          type="password"
          value={confirmation}
        />

        <Button loading={isSubmitting} type="submit">
          Salvar senha
        </Button>
      </form>
    </AuthLayout>
  );
}
